import api, { IPaginationResponse, IPaginationParams } from 'config/axios';
import AppService from 'common/app-service';
import {
  IAccount,
  EAccountType,
  IAccountDashboardBalance,
  EAccountStatus,
  IPersonTermsAcceptance,
  IOnboardingOverview,
} from 'common/accounts.interface';
import { CreateBankingAccount } from 'common/create-banking-account.dto';
import { ITransaction } from 'common/transactions.interface';
import { IWithdrawalRequestPayload, IWithdrawalRequest } from 'common/withdrawal-request.interface';
import { ICounterparty } from 'common/secondary-operation.interface';
import { IBeneficiary } from 'common/beneficiary.interface';
import { IBankSlip } from 'common/bank-slip.interface';
import {
  EPixOperationType,
  IPixKeyTransactionPayload,
  IBeneficiaryTransactionPayload,
  IBankDataTransactionPayload,
  IPamcardTransactionPayload,
  IBankSlipTransactionPayload,
  IApproverInfo,
  ICreateBankSlipRequest,
  IQRCode,
  ICreateQRCodeResponse,
} from 'types/banking';

export default class BankingService extends AppService {
  constructor() {
    super('banking', api);
  }

  async getAccounts(params?: {
    carrierBranchId?: number;
    accountType?: EAccountType;
    status?: EAccountStatus;
    tradeName?: string;
    businessName?: string;
  }): Promise<IAccount[]> {
    const { data } = await this.api.get(this.root('accounts'), { params });
    return data;
  }

  async getAccountsByCarrierRoot(carrierRootId: number): Promise<IAccount[]> {
    const { data } = await this.api.get(this.root(`accounts/carrier-root/${carrierRootId}`));
    return data;
  }

  async createAccount(params: CreateBankingAccount): Promise<IAccount> {
    const { data } = await this.api.post(this.root('accounts'), params);
    return data;
  }

  async getAccountsByCarrierBranch(carrierBranchId: number): Promise<IAccount[]> {
    const { data } = await this.api.get(this.root(`accounts/carrier-branch/${carrierBranchId}`));
    return data;
  }

  async createAccountForCarrierBranch(carrierBranchId: number, params: CreateBankingAccount): Promise<IAccount> {
    const { data } = await this.api.post(this.root(`accounts/carrier-branch/${carrierBranchId}`), params);
    return data;
  }

  async getOnboardingOverview(): Promise<IOnboardingOverview> {
    const { data } = await this.api.get(this.root('accounts/onboarding-overview'));
    return data;
  }

  async getTermsAcceptanceByCarrierBranch(carrierBranchId: number): Promise<IPersonTermsAcceptance[]> {
    const { data } = await this.api.get(`/terms-acceptance/carrier-branch/${carrierBranchId}`);
    return data;
  }

  async activateAccount(accountId: number): Promise<IAccount> {
    const { data } = await this.api.patch(this.root(`accounts/${accountId}/activate`));
    return data;
  }

  async getTransactions(params?: {
    accountType?: EAccountType;
    startDate?: string;
    endDate?: string;
    payDueDateStart?: string;
    payDueDateEnd?: string;
    type?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<IPaginationResponse<ITransaction>> {
    const { data } = await this.api.get(this.root('transactions'), { params });
    return data;
  }

  async getTransactionById(id: number): Promise<ITransaction> {
    const { data } = await this.api.get(this.root(`transactions/${id}`));
    return data;
  }

  async getExternalApprovalTransaction(id: number, token: string): Promise<ITransaction> {
    const { data } = await this.api.get(this.root(`transactions/external-approval/${id}`), {
      params: { token, isPublicRequest: true },
    });
    return data;
  }

  async approveExternalTransaction(id: number, token: string): Promise<ITransaction> {
    const { data } = await this.api.post(
      this.root(`transactions/external-approval/${id}/approve`),
      { token },
      { params: { isPublicRequest: true } }
    );
    return data;
  }

  async rejectExternalTransaction(id: number, token: string): Promise<ITransaction> {
    const { data } = await this.api.post(
      this.root(`transactions/external-approval/${id}/reject`),
      { token },
      { params: { isPublicRequest: true } }
    );
    return data;
  }

  async resendExternalApprovalEmail(id: number, token: string): Promise<{ message: string }> {
    const { data } = await this.api.post(
      this.root(`transactions/external-approval/${id}/resend`),
      { token },
      { params: { isPublicRequest: true } }
    );
    return data;
  }

  async getApproversByCarrierBranch(): Promise<IApproverInfo[]> {
    const { data } = await this.api.get(this.root(`transactions/approvers`));
    return data;
  }

  async requestOtp(): Promise<{ message: string; code?: string }> {
    const { data } = await this.api.post(this.root('transactions/otp/request'));
    return data;
  }

  async approveTransaction(id: number, otp: string): Promise<ITransaction> {
    const { data } = await this.api.post(this.root(`transactions/${id}/approve`), { otp });
    return data;
  }

  async rejectTransaction(id: number, otp: string): Promise<ITransaction> {
    const { data } = await this.api.post(this.root(`transactions/${id}/reject`), { otp });
    return data;
  }

  async getAccountsBalance(): Promise<{
    accountId: number;
    balance: number;
  }> {
    const { data } = await this.api.get(this.root(`accounts/balance`));
    return data;
  }

  async getDashboardBalance(): Promise<IAccountDashboardBalance[]> {
    const { data } = await this.api.get(this.root('accounts/dashboard/balance'));
    return data;
  }

  async getWithdrawalRequests(params: IPaginationParams): Promise<IPaginationResponse<IWithdrawalRequest>> {
    const { data } = await this.api.get(this.root('withdrawal-requests'), { params });
    return data;
  }

  async createWithdrawalRequest(payload: IWithdrawalRequestPayload): Promise<IWithdrawalRequest> {
    const { data } = await this.api.post(this.root('withdrawal-requests'), payload);
    return data;
  }

  async approveWithdrawalRequest(id: number, commentary?: string): Promise<IWithdrawalRequest> {
    const { data } = await api.post(this.root(`withdrawal-requests/${id}/approve`), { commentary });
    return data;
  }

  async rejectWithdrawalRequest(id: number, commentary?: string): Promise<IWithdrawalRequest> {
    const { data } = await api.post(this.root(`withdrawal-requests/${id}/reject`), { commentary });
    return data;
  }

  async getAccountIncomeQuarters(): Promise<{ year: number; quarters: number[] }[]> {
    const { data } = await this.api.get(this.root(`accounts/meta/quarters`));

    return data;
  }

  async exportIncomeReport(payload: { year: number; quarter: number; accountType: string }): Promise<{ message: string }> {
    const { data } = await this.api.post(this.root('export/income'), {
      ...payload,
      account_type: payload.accountType,
    });

    return data;
  }

  async exportTransactionsReport(payload: { startDate: string; endDate: string; accountType: string }): Promise<{ message: string }> {
    const { data } = await this.api.post(this.root('export/transactions'), {
      start_date: payload.startDate,
      end_date: payload.endDate,
      account_type: payload.accountType,
    });

    return data;
  }

  async downloadTransactionsSheet(payload: { startDate: string; endDate: string; accountType: string }): Promise<Blob> {
    const { data } = await this.api.post(
      this.root('export/transactions/download'),
      {
        start_date: payload.startDate,
        end_date: payload.endDate,
        account_type: payload.accountType,
      },
      { responseType: 'blob' }
    );

    return data;
  }

  async getCounterpartyBanking(): Promise<ICounterparty[]> {
    const { data } = await this.api.get('/counterparty/banking');
    return data;
  }

  async getInstitutionsList(name?: string): Promise<{ name: string; ispb: string; code?: string }[]> {
    const { data } = await this.api.get(this.root('institutions'), { params: { ...(name && { name }) } });
    return data;
  }

  async getBeneficiaries(params?: IPaginationParams): Promise<IPaginationResponse<IBeneficiary>> {
    const { data } = await this.api.get(this.root('beneficiaries'), { params });
    return data;
  }

  async createTransaction(operationType: EPixOperationType.PIX_KEY, payload: IPixKeyTransactionPayload): Promise<ITransaction>;

  async createTransaction(operationType: EPixOperationType.BENEFICIARY, payload: IBeneficiaryTransactionPayload): Promise<ITransaction>;

  async createTransaction(operationType: EPixOperationType.BANK_DATA, payload: IBankDataTransactionPayload): Promise<ITransaction>;

  async createTransaction(operationType: EPixOperationType.PAMCARD, payload: IPamcardTransactionPayload): Promise<ITransaction>;

  async createTransaction(operationType: EPixOperationType.BANK_SLIP, payload: IBankSlipTransactionPayload): Promise<ITransaction>;

  async createTransaction(
    operationType: EPixOperationType,
    payload:
      | IPixKeyTransactionPayload
      | IBeneficiaryTransactionPayload
      | IBankDataTransactionPayload
      | IPamcardTransactionPayload
      | IBankSlipTransactionPayload
  ): Promise<ITransaction> {
    const transactionPayload: any = { ...payload };

    // For BANK_SLIP operations, detect format and send appropriate field
    if (operationType === EPixOperationType.BANK_SLIP) {
      const bankSlipPayload = payload as IBankSlipTransactionPayload;

      if (bankSlipPayload.barCode) {
        const formatType = this.detectBankSlipFormat(bankSlipPayload.barCode);
        delete transactionPayload.barCode;

        if (formatType === 'barcode') {
          transactionPayload.barCode = bankSlipPayload.barCode;
        } else {
          transactionPayload.digitable = bankSlipPayload.barCode;
        }
      }
    }

    const { data } = await this.api.post(this.root('transactions'), transactionPayload, { params: { operation_type: operationType } });
    return data;
  }

  async searchBankSlip(barCode: string): Promise<IBankSlip> {
    // Detect format
    const formatType = this.detectBankSlipFormat(barCode);

    const { data } = await this.api.post(this.root('transactions/bank-slip/search'), {
      bar_code: barCode,
      format_type: formatType,
    });

    return data;
  }

  async getBankSlips(params?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    type?: string;
    page?: number;
    limit?: number;
  }): Promise<IPaginationResponse<ITransaction>> {
    const { data } = await this.api.get(this.root('transactions/bank-slips'), { params });
    return data;
  }

  async createBankSlip(payload: ICreateBankSlipRequest): Promise<ITransaction> {
    const { data } = await this.api.post(this.root('transactions/bank-slip/create'), payload);

    return data;
  }

  async getPixKeyInfo(payload: { pixKey: string; accountNumber: string; taxNumber?: string }): Promise<any> {
    const { data } = await this.api.post(this.root('transactions/pix-key/info'), {
      pixKey: payload.pixKey,
      accountNumber: payload.accountNumber,
      ...(payload.taxNumber && { taxNumber: payload.taxNumber }),
    });

    return data;
  }

  private detectBankSlipFormat(barCode: string): 'barcode' | 'digitable_line' {
    // Remove spaces and non-digits
    const cleaned = barCode.replace(/\D/g, '');

    // Traditional boleto barcode: 44 digits; QR/PIX barcode: 12-13 digits
    if (cleaned.length === 44 || (cleaned.length >= 12 && cleaned.length <= 13)) {
      return 'barcode';
    }

    // Digitable line: 47 digits (traditional boleto) or other variable PIX formats
    if (cleaned.length >= 20) {
      return 'digitable_line';
    }

    // Default to digitable_line for validation on backend
    return 'digitable_line';
  }

  async createStaticQRCode(): Promise<ICreateQRCodeResponse> {
    const { data } = await this.api.post(this.root('qrcodes/static'));
    return data;
  }

  async createDynamicQRCode(amount: number): Promise<ICreateQRCodeResponse> {
    const { data } = await this.api.post(this.root('qrcodes/dynamic'), { amount });
    return data;
  }

  async getQRCode(id: string): Promise<IQRCode> {
    const { data } = await this.api.get(this.root(`qrcodes/${id}`));
    return data;
  }

  async listQRCodes(): Promise<IQRCode[]> {
    const { data } = await this.api.get(this.root('qrcodes'));
    return data;
  }

  async getPixKeys(): Promise<{ pixKeyEvp: string | null; pixKeyDoc: string | null; pixKeyEmail: string | null }> {
    const { data } = await this.api.get(this.root('transactions/pix-keys'));
    return data;
  }

  async requestCreatePixKey(payload: { keyType: string; key: string }): Promise<{ message: string }> {
    const { data } = await this.api.post(this.root('transactions/pix-keys'), payload);
    return data;
  }

  async requestDeletePixKey(key: string, keyType: string): Promise<{ message: string }> {
    const { data } = await this.api.delete(this.root('transactions/pix-keys'), {
      params: { key, key_type: keyType },
    });
    return data;
  }

  async deleteStaticQRCode(id: string): Promise<void> {
    await this.api.delete(this.root(`qrcodes/static/${id}`));
  }

  async deleteDynamicQRCode(id: string): Promise<void> {
    await this.api.delete(this.root(`qrcodes/dynamic/${id}`));
  }

  async getBankSlipPdf(transactionId: number): Promise<{ url: string; filename: string }> {
    const { data } = await this.api.get(this.root(`transactions/bank-slips/${transactionId}/pdf`));
    return data;
  }
}
